import apiClient from './client'
import { AxiosResponse, AxiosRequestConfig } from 'axios'

// Helper to remove undefined values from params
function cleanParams(params?: object): object | undefined {
  if (!params) return undefined

  const cleaned: any = {}
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      cleaned[key] = value
    }
  })

  return Object.keys(cleaned).length > 0 ? cleaned : undefined
}

export class ApiService {
  // GET request
  static async get<T>(url: string, paramsOrConfig?: object | AxiosRequestConfig): Promise<T> {
    // Check if this is a config object (has headers, params, etc.) or just params
    const isConfig = paramsOrConfig && ('headers' in paramsOrConfig || 'params' in paramsOrConfig)

    if (isConfig) {
      // It's a config object, use it directly
      const response: AxiosResponse<T> = await apiClient.get(url, paramsOrConfig as AxiosRequestConfig)
      return response.data
    } else {
      // It's just params, wrap them
      const cleanedParams = cleanParams(paramsOrConfig as object)
      const response: AxiosResponse<T> = await apiClient.get(url, { params: cleanedParams })
      return response.data
    }
  }

  // POST request
  static async post<T>(url: string, data?: object, config?: any): Promise<T> {
    const response: AxiosResponse<T> = await apiClient.post(url, data, config)
    return response.data
  }

  // PUT request
  static async put<T>(url: string, data?: object): Promise<T> {
    const response: AxiosResponse<T> = await apiClient.put(url, data)
    return response.data
  }

  // PATCH request
  static async patch<T>(url: string, data?: object): Promise<T> {
    const response: AxiosResponse<T> = await apiClient.patch(url, data)
    return response.data
  }

  // DELETE request
  static async delete<T>(url: string, data?: object): Promise<T> {
    const response: AxiosResponse<T> = await apiClient.delete(url, { data })
    return response.data
  }

  // Upload file (multipart/form-data)
  // Don't set Content-Type header - let browser set it with boundary
  static async upload<T>(url: string, formData: FormData): Promise<T> {
    const response: AxiosResponse<T> = await apiClient.post(url, formData, {
      headers: {
        'Content-Type': undefined, // Remove default JSON content-type
      },
    })
    return response.data
  }
}

export default ApiService
